<template>
  <div class="user-management">
    <el-card shadow="never" class="main-card animate__animated animate__fadeInUp">
      <!-- 搜索栏 + 累计收益（同一行） -->
      <div class="toolbar-row">
        <div class="search-group">
          <el-input
            v-model="searchUserId"
            placeholder="用户ID"
            clearable
            size="small"
            style="width: 140px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchNickname"
            placeholder="搜索昵称"
            prefix-icon="el-icon-search"
            clearable
            size="small"
            style="width: 180px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchInviteCode"
            placeholder="邀请码"
            prefix-icon="el-icon-collection-tag"
            clearable
            size="small"
            style="width: 160px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select
            v-model="searchStatus"
            placeholder="用户状态"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部" :value="''" />
            <el-option label="未封禁" :value="0" />
            <el-option label="已封禁" :value="1" />
          </el-select>
          <el-select
            v-model="searchRiskLevel"
            placeholder="风险等级"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部风险等级" :value="''" />
            <el-option label="无风险" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="refreshList">刷新</el-button>
        </div>
        <div class="earned-summary">
          <i class="el-icon-coin"></i>
          <span class="earned-label">累计总收益</span>
          <span class="earned-value">{{ totalEarned }}</span>
          <span class="earned-unit">金币</span>
        </div>
      </div>

      <div v-if="error" class="error-msg">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="searchUserId || searchNickname || searchInviteCode ? '没有符合条件的用户' : '暂无用户数据'"
      />

      <div v-else class="table-wrapper">
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="data-table"
          row-key="id"
          :header-cell-style="{ background: '#fafafa', color: '#303133', fontWeight: 600 }"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="openid" label="OpenID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="nickname" label="昵称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="avatarUrl" label="头像" width="80" align="center">
            <template slot-scope="scope">
              <el-avatar v-if="scope.row.avatarUrl" :key="scope.row.id" :src="scope.row.avatarUrl" :size="32" shape="circle" />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="inviteCode" label="邀请码" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain">{{ scope.row.inviteCode || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="leaderId" label="代理ID" width="80" align="center" />
          <el-table-column prop="parentId" label="上级ID" width="80" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small" effect="plain">封禁</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="riskLevel" label="风险" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.riskLevel === 1" type="success" size="small">低</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 2" type="warning" size="small">中</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 3" type="danger" size="small">高</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-view"
                @click="showDetail(scope.row)"
              >
                详情
              </el-button>
              <el-button
                v-if="canEdit"
                size="mini"
                :type="scope.row.status === 0 ? 'danger' : 'success'"
                plain
                @click="handleToggleStatus(scope.row)"
              >
                {{ scope.row.status === 0 ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 用户详情弹窗 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" element-loading-text="加载中..." class="detail-content">
        <div class="detail-header">
          <el-avatar v-if="detailUser.avatarUrl" :src="detailUser.avatarUrl" :size="56" shape="circle" />
          <div class="detail-name">{{ detailUser.nickname || '-' }}</div>
          <div class="detail-id">ID: {{ detailUser.id }}</div>
        </div>
        <div class="detail-cards">
          <div class="detail-card coin-current">
            <div class="detail-card-label">当前余额</div>
            <div class="detail-card-value">{{ detailUser.coinBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-total">
            <div class="detail-card-label">累计余额</div>
            <div class="detail-card-value">{{ detailUser.totalEarnedCoins || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
        </div>
        <div v-if="detailUser.status === 1 && detailUser.banDes" class="detail-ban-reason">
          <div class="ban-reason-label">封禁原因</div>
          <div class="ban-reason-text">{{ detailUser.banDes }}</div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 封禁用户弹窗 -->
    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="banUser ? banUser.nickname || banUser.id : ''" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUserId: '',
      searchNickname: '',
      searchInviteCode: '',
      searchStatus: '',
      searchRiskLevel: '',
      totalEarned: 0,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      banDialogVisible: false,
      banUser: null,
      banForm: {
        status: 1,
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 是否有修改权限（仅超级管理员 role=0）
    canEdit() {
      return parseInt(localStorage.getItem('role')) === 0
    }
  },
  mounted() {
    this.fetchUsers()
    this.fetchTotalEarned()
  },
  methods: {
    async fetchTotalEarned() {
      try {
        const result = await get('/admin/users/total-earned')
        this.totalEarned = (result && result.data != null) ? result.data : 0
      } catch (e) {
        console.error('获取累计总收益失败:', e.message)
      }
    },

    async showDetail(user) {
      this.detailUser = {
        id: user.id,
        nickname: user.nickname,
        avatarUrl: user.avatarUrl
      }
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.detailUser = {
          id: data.id || user.id,
          nickname: data.nickname || user.nickname,
          avatarUrl: data.avatarUrl || user.avatarUrl,
          coinBalance: data.coinBalance,
          totalEarnedCoins: data.totalEarned,
          banDes: data.banDes || '',
          status: data.status !== undefined ? data.status : user.status
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchUserId) {
          params.userId = this.searchUserId
        }
        if (this.searchNickname) {
          params.nickname = this.searchNickname
        }
        if (this.searchInviteCode) {
          params.inviteCode = this.searchInviteCode
        }
        if (this.searchStatus !== '' && this.searchStatus !== null) {
          params.status = this.searchStatus
        }
        if (this.searchRiskLevel !== '' && this.searchRiskLevel !== null) {
          params.riskLevel = this.searchRiskLevel
        }

        const result = await get('/admin/users', params)
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchNickname = ''
      this.searchInviteCode = ''
      this.searchStatus = ''
      this.searchRiskLevel = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleToggleStatus(user) {
      if (user.status === 0) {
        // 封禁用户：弹出对话框填写封禁描述
        this.banUser = user
        this.banForm.status = 1
        this.banForm.banDes = ''
        this.banDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
        })
      } else {
        // 解封用户：直接发送
        this.doUpdateStatus(user, 0)
      }
    },
    async doUpdateStatus(user, status, banDes = null) {
      const actionText = status === 1 ? '封禁' : '解封'
      try {
        const payload = { status }
        if (banDes) payload.banDes = banDes
        const res = await put(`/admin/users/${user.id}`, payload)
        this.$message.success(res.msg || `${actionText}成功`)
        user.status = status
        if (status === 0) user.banDes = null
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },
    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      this.banDialogVisible = false
      await this.doUpdateStatus(this.banUser, this.banForm.status, this.banForm.banDes)
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 4px;
}

.main-card {
  border-radius: 8px;
}

/* ===== 工具栏行 ===== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

/* ===== 累计收益 ===== */
.earned-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 8px;
  border: 1px solid #d1fae5;
  white-space: nowrap;
  flex-shrink: 0;
}

.earned-summary .el-icon-coin {
  font-size: 18px;
  color: #e6a23c;
  margin-right: 4px;
}

.earned-label {
  font-size: 13px;
  color: #606266;
  margin-right: 4px;
}

.earned-value {
  font-size: 20px;
  font-weight: 700;
  color: #059669;
  letter-spacing: 0.5px;
}

.earned-unit {
  font-size: 12px;
  color: #909399;
}

/* ===== 错误信息 ===== */
.error-msg {
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

/* ===== 表格 ===== */
.table-wrapper {
  margin-top: 4px;
}

.data-table {
  border-radius: 6px;
  overflow: hidden;
}

.data-table::before {
  display: none;
}

.text-muted {
  color: #c0c4cc;
}

/* ===== 分页 ===== */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 8px 0;
}

/* ===== 详情弹窗 ===== */
.detail-content {
  padding: 8px 0;
}

.detail-header {
  text-align: center;
  padding: 12px 0 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-top: 10px;
}

.detail-id {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.detail-cards {
  display: flex;
  gap: 16px;
  padding: 0 4px;
}

.detail-card {
  flex: 1;
  text-align: center;
  padding: 20px 12px;
  border-radius: 10px;
  border: 1px solid;
}

.detail-card-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.detail-card-value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.detail-card-unit {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.coin-current {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.coin-current .detail-card-value {
  color: #16a34a;
}

.coin-total {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.coin-total .detail-card-value {
  color: #2563eb;
}

/* ===== 封禁原因 ===== */
.detail-ban-reason {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ban-reason-label {
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 4px;
}

.ban-reason-text {
  font-size: 14px;
  color: #991b1b;
  line-height: 1.5;
}

.dialog-footer {
  text-align: center;
}

/* ===== 动画 ===== */
.main-card.animate__animated,
.data-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
